import { computed, ref } from 'vue'
import { API_TYPE_CATALOG } from '@/common/converter/const'
import { getCategorySub } from '@/common/requests/data-fetch/get-category-sub'
import { getApiType } from '@/common/utils/home/get-api-type'
import { apiRequest } from '@/common/utils/request.common'
import { HTTP_METHOD_GET } from '@/common/utils/request.const'
import {
  ROUTE_NAME_MAIN_CATEGORY,
  ROUTE_NAME_SUB_CATEGORY,
} from '@/common/resources/router.const'
import { useCatalog } from '@/common/use/useCatalog'
import { dissectHome } from '@/common/converter/utils/dissect-home'
import { convertListSource } from '@/common/converter/convert-list-source'
import { useRouterComposable } from '@/common/use/useRouterComposable'

export const useCategorySub = () => {
  const { getCatalog } = useCatalog()
  const { pushRouter, replaceRouter: replaceRouterQuery } =
    useRouterComposable()

  const listData = ref({}) // 구형 리스트 데이터 규격
  const cellList = ref([]) // 구형 셀 리스트 규격
  const apiType = ref('') // api 타입 band | catalog

  const isApiTypeCatalog = computed(() => apiType.value === API_TYPE_CATALOG)

  /**
   * 카테고리와 서브카테고리를 받아 서브카테고리의 데이터를 받아 구형 리스트 데이터로 변환 후 반환
   * @param category
   * @param subCategory
   * @returns {Promise<void|undefined>}
   */
  const getSubCategoryHomeData = async (category, subCategory) => {
    try {
      const response = await getCategorySub({ category, subCategory })

      if (!response) {
        return undefined
      }

      return response?.data
    } catch (error) {
      console.error('Error fetching category sub data:', error)
      throw new Error(error)
    }
  }

  /**
   * 카탈로그의 api 타입을 가져옴
   * @param layoutCatalog
   */
  const setApiType = (layoutCatalog) => {
    const catalogList = layoutCatalog?.catalogList ?? []
    const [catalog] = catalogList

    if (catalog) {
      apiType.value = getApiType(catalog?.elementList)
    }
  }

  /**
   * fetch URL 을 받아 api 타입을 참조하여 list 데이터를 반환
   * @param fetchUrl
   * @param homeData
   * @returns {Promise<undefined>|Promise<undefined|any>}
   */
  const fetchData = ({ fetchUrl, homeData }) => {
    if (!fetchUrl) {
      return undefined
    }

    return isApiTypeCatalog.value
      ? fetchCatalogData(fetchUrl, homeData)
      : fetchListData(fetchUrl)
  }

  /**
   * 구형 리스트 데이터 규격 용 fetch
   * @param fetchUrl
   * @returns {Promise<undefined|any>}
   */
  const fetchListData = async (fetchUrl = '') => {
    try {
      const response = await apiRequest({
        url: `https://${fetchUrl}`,
        method: HTTP_METHOD_GET,
      })

      if (!response) {
        return undefined
      }

      return response?.data
    } catch (error) {
      console.error('Error fetchListData:', error)
      return undefined
    }
  }

  /**
   * 신형 카탈로그 규격 용 fetch - home api 인자가 추가로 필요
   * @param fetchUrl
   * @param homeData
   * @returns {Promise<{note: *, type: *, sub_type: *, version: *, gen_time: string, cell_toplist: {blurtarget_img: string, cta: string, bgcolor: string, pagecount: string, count: string, title_list: *[]}, filter: {}, tvcut_bybroadcaster_list: {}}|undefined>}
   */
  const fetchCatalogData = async (fetchUrl = '', homeData = {}) => {
    try {
      const { data: target } = await getCatalog(fetchUrl)
      const {
        filter = {},
        uiType = '',
        subType = '',
        source,
      } = dissectHome(homeData)

      return convertListSource({
        filter,
        uiType,
        subType,
        source,
        target,
      })
    } catch (error) {
      console.error('Error fetchCatalogData:', error)
      return undefined
    }
  }

  /**
   * main category 로 이동
   * @param main
   */
  const goMainCategory = ({ main = '' }) => {
    pushRouter({
      name: ROUTE_NAME_MAIN_CATEGORY,
      query: { mainCode: main },
    })
  }

  /**
   * sub category 로 라우터 push
   * @param query
   */
  const updateRouter = (query) => {
    pushRouter({
      name: ROUTE_NAME_SUB_CATEGORY,
      query,
    })
  }

  /**
   * sub category 로 라우터 replace
   * @param query
   */
  const replaceRouter = (query) => {
    replaceRouterQuery({ query })
  }

  return {
    listData,
    cellList,
    apiType,
    isApiTypeCatalog,
    getSubCategoryHomeData,
    setApiType,
    fetchData,
    fetchListData,
    fetchCatalogData,
    goMainCategory,
    updateRouter,
    replaceRouter,
  }
}
